import { Form, Formik } from "formik";
import { useState } from "react";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from "@material-ui/core";

import { useTenant } from "src/components/LoggedInAdminProvider";
import FormikErrorBanner from "src/components/form/FormikErrorBanner";
import FormikTextField from "src/components/form/FormikTextField";
import { createOptOutVariables } from "src/graphql/generated/createOptOut";
import { getErrorMessage } from "src/util/errors";
import yup from "src/util/yup";

export type AddOptOutValues = Omit<createOptOutVariables, "subaccountId">;

export default function AddOptOutDialog({
  open,
  onClose,
  onSubmit
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: AddOptOutValues) => Promise<void>;
}): JSX.Element {
  const tenant = useTenant();
  const [saveErrors, setSaveErrors] = useState<string | null>(null);

  const handleSubmit = async (payload: AddOptOutValues) => {
    setSaveErrors(null);

    try {
      await onSubmit(payload);
    } catch (e) {
      setSaveErrors(getErrorMessage(e));
    }
  };

  return (
    <Dialog open={open} fullWidth maxWidth="xs">
      <DialogTitle>Add Number to Do Not Call List</DialogTitle>
      <Formik
        initialValues={{ phoneNumber: "" }}
        validationSchema={yup.object().shape({
          phoneNumber: yup
            .string()
            .required("Required")
            .phoneNumber(tenant?.countryIso)
        })}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting }) => (
          <Form>
            <DialogContent>
              <FormikErrorBanner
                renderBanner={errors => {
                  if (errors.saveErrors) {
                    return `Error saving: ${errors.saveErrors}`;
                  }
                  return "Please fix the errors indicated below.";
                }}
                additionalErrors={saveErrors}
              />
              <FormikTextField
                fullWidth
                name="phoneNumber"
                label="Phone number"
              />
            </DialogContent>
            <DialogActions>
              <Button disabled={isSubmitting} onClick={onClose} color="primary">
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                color="primary"
                variant="contained"
              >
                {isSubmitting ? "Saving..." : "Save"}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}
